import React, { useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import { useDispatch } from "react-redux";
import {
  postCommissionsStructures,
  editCommissionStructure,
} from "../../../../slices/national/thunk";
import Swal from "sweetalert2";

const AddCommissionStructureModal = ({
  isOpen,
  setIsOpen,
  nationalId,
  onRefresh,
  totalAmountCommission,
}) => {
  const dispatch = useDispatch();

  const totalAmountParsed = (totalAmountCommission / 100).toFixed(2);

  const [formData, setFormData] = useState({
    name: "",
    national: "",
    ga: "",
    broker: "",
  });

  const [loading, setLoading] = useState(false);

  const handleNumericInput = (value) => {
    const cleanValue = value.replace(/[^0-9]/g, "");
    if (!cleanValue) return "";
    return (Number(cleanValue) / 100).toFixed(2);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // No aplicar formateo para el campo "name"
    const formattedValue = name === "name" ? value : handleNumericInput(value);

    setFormData((prevData) => ({
      ...prevData,
      [name]: formattedValue,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const total =
      Number(formData.national) * 100 +
      Number(formData.ga) * 100 +
      Number(formData.broker) * 100;

    if (total !== totalAmountCommission) {
      Swal.fire({
        title: "Error!",
        text: `The total of National, GA, and Broker must equal $ ${totalAmountParsed}`,
        icon: "error",
        confirmButtonText: "OK",
      });
      setLoading(false);
      return;
    }

    try {
      const payload = {
        name: formData.name,
        national: Number(formData.national) * 100,
        ga: Number(formData.ga) * 100,
        broker: Number(formData.broker) * 100,
      };

      const response = await dispatch(
        postCommissionsStructures({ id: nationalId, data: payload })
      );
      if (response && !response.error) {
        Swal.fire({
          title: "Success!",
          text: "Commission structure has been added successfully.",
          icon: "success",
          confirmButtonText: "OK",
        });

        setIsOpen(false);
        onRefresh();
        setFormData({
          name: "",
          national: "",
          ga: "",
          broker: "",
        });
      } else {
        Swal.fire({
          title: "Error!",
          text: "There was an error adding the commission structure. Please try again.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text: "There was an error adding the commission structure. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditCommissionStructure = async () => {
    setLoading(true);

    const total =
      Number(formData.national) * 100 +
      Number(formData.ga) * 100 +
      Number(formData.broker) * 100;

    if (total !== 1000) {
      Swal.fire({
        title: "Error!",
        text: "The total of National, GA, and Broker must equal $10.00.",
        icon: "error",
        confirmButtonText: "OK",
      });
      setLoading(false);
      return;
    }

    try {
      const payload = {
        name: formData.name,
        national: Number(formData.national) * 100,
        ga: Number(formData.ga) * 100,
        broker: Number(formData.broker) * 100,
      };

      const response = await dispatch(
        editCommissionStructure({ id: nationalId, data: payload })
      );
      if (response && !response.error) {
        Swal.fire({
          title: "Success!",
          text: "Commission structure has been updated successfully.",
          icon: "success",
          confirmButtonText: "OK",
        });

        setIsOpen(false);
        onRefresh();
        setFormData({
          name: "",
          national: "",
          ga: "",
          broker: "",
        });
      } else {
        Swal.fire({
          title: "Error!",
          text: "There was an error updating the commission structure. Please try again.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text: "There was an error updating the commission structure. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={() => setIsOpen(!isOpen)}>
      <ModalHeader toggle={() => setIsOpen(!isOpen)}>
        Add Commission Structure
      </ModalHeader>
      <ModalBody>
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label for="name">Name</Label>
            <Input
              type="text"
              name="name"
              id="name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </FormGroup>
          <div className="mt-4 mb-3">
            <h6>$ {totalAmountParsed} to assign</h6>
          </div>
          <FormGroup>
            <Label for="national">National</Label>
            <InputGroup>
              <InputGroupText>$</InputGroupText>
              <Input
                type="text"
                name="national"
                id="national"
                value={formData.national}
                onChange={handleInputChange}
                required
              />
            </InputGroup>
          </FormGroup>
          <FormGroup>
            <Label for="ga">GA</Label>
            <InputGroup>
              <InputGroupText>$</InputGroupText>
              <Input
                type="text"
                name="ga"
                id="ga"
                value={formData.ga}
                onChange={handleInputChange}
                required
              />
            </InputGroup>
          </FormGroup>
          <FormGroup>
            <Label for="broker">Broker</Label>
            <InputGroup>
              <InputGroupText>$</InputGroupText>
              <Input
                type="text"
                name="broker"
                id="broker"
                value={formData.broker}
                onChange={handleInputChange}
                required
              />
            </InputGroup>
          </FormGroup>
          <ModalFooter>
            <Button color="primary" type="submit" disabled={loading}>
              {loading ? <Spinner size="sm" /> : "Add Commission Structure"}
            </Button>
          </ModalFooter>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default AddCommissionStructureModal;
